import type { EquityDataRequestForAPIClient } from "@/interfaces/dataRequest/equity";
import ShowToaster from "@/toaster";
import {
  getRequestObjectForAPI,
  getRequestObjectForAPIForPrePost,
} from "@/utils/dataRequest/equity";
import {
  getRequestObjectForReputationAPI,
  getRequestObjectForReputationAPIForPrePost,
} from "@/utils/dataRequest/reputation";
import {
  getRequestObjectForVisibilityAPI,
  getRequestObjectForVisibilityAPIForPrePost,
} from "@/utils/dataRequest/visibility";
import { getPrevPeriodDateRange } from "@/utils/date";
import { apiInstances } from "@endpoints/config";
import {
  ADVERTISING_START_DATE,
  REPUTATION_START_DATE,
} from "@/constant/constantsThrougoutProject";
import { getAbortSignalForKey, clearAbortController } from "./requestManager";

const dataService = {
  equity: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "trend/equity";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "equity";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "prepost";
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
    },
  },
  conversion: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "trend/conversion";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "conversion";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "prepost";
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
    },
  },
  emotional: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "trend/emotional";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "emotional";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "prepost";
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
    },
  },
  advertising: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "trend/advertising";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "advertising";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
              restrictedDate: ADVERTISING_START_DATE,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "prepost";
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      profile: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "profile";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
    },
  },
  visibility: {
    getSegmentData: async (requestPayload: any) => {
      const url = `visibility/segment`;
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: ADVERTISING_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
      } catch (error: any) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getBrandData: async (requestPayload: any) => {
      const url = `visibility/brand`;
      try {
        if (requestPayload.viewMode === "PRE_POST") {
          requestPayload =
            getRequestObjectForVisibilityAPIForPrePost(requestPayload);
        } else if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const prevDates = getPrevPeriodDateRange({
          startDate: requestPayload?.fromDate,
          endDate: requestPayload?.toDate,
          statTest: requestPayload?.statTesting,
          restrictedDate: ADVERTISING_START_DATE,
        });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate: prevDates?.prevPeriodStartDate,
            prevPeriodEndDate: prevDates?.prevPeriodEndDate,
          },
        };
      } catch (error: any) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getMarketData: async (requestPayload: any) => {
      const url = `visibility/market`;
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: ADVERTISING_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
      } catch (error: unknown) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getxlsxExport: async (requestPayload: any) => {
      const url = `export/visibility/export?fileType=xlsx`;
      try {
        const response = await apiInstances.export
          .post(url, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getpptExport: async (requestPayload: any) => {
      const url = `dataexport/visibility/export?fileType=pptx`;
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .text();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
  },
  reputation: {
    getSegmentData: async (requestPayload: any) => {
      const url = `rq/segment`;
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: REPUTATION_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
      } catch (error: any) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getBrandData: async (requestPayload: any) => {
      const url = `rq/brand`;
      try {
        if (requestPayload.viewMode === "PRE_POST") {
          requestPayload =
            getRequestObjectForReputationAPIForPrePost(requestPayload);
        } else if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const prevDates = getPrevPeriodDateRange({
          startDate: requestPayload?.fromDate,
          endDate: requestPayload?.toDate,
          statTest: requestPayload?.statTesting,
          restrictedDate: REPUTATION_START_DATE,
        });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate: prevDates?.prevPeriodStartDate,
            prevPeriodEndDate: prevDates?.prevPeriodEndDate,
          },
        };
      } catch (error: any) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getMarketData: async (requestPayload: any) => {
      const url = `rq/market`;
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: REPUTATION_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
      } catch (error: unknown) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getxlsxExport: async (requestPayload: any) => {
      const url = `export/RQ/export?fileType=xlsx`;
      try {
        const response = await apiInstances.export
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .text();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getpptExport: async (requestPayload: any) => {
      const url = `dataexport/RQ/export?fileType=pptx`;
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .text();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
  },
  profileFilters: {
    metric: async (geoIds: any) => {
      const url = "profile/filters";
      try {
        const response = await apiInstances.data
          .post(url, {
            json: geoIds,
            signal: getAbortSignalForKey(url),
          })
          .json();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    response: async (requestPayload: any, metricId: any) => {
      const url = "profile/filteroptions";
      try {
        const response = await apiInstances.data
          .post(`profile/filteroptions/${metricId}`, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
  },
  questions: {
    getQuestionsList: async (userId: number, marketId: number) => {
      const url = "questions/getQuestionsList";
      try {
        const response = await apiInstances.data
          .get(`questions/${userId}?marketId=${marketId}`, {
            signal: getAbortSignalForKey(url),
          })
          .json();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return [];
      } finally {
        clearAbortController(url);
      }
    },
    getSegmentData: async (requestPayload: any) => {
      const url = "questions/segment";
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        return response;
      } catch (error: unknown) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getBrandData: async (requestPayload: any) => {
      const url = "questions/brand";
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        return response;
      } catch (error: unknown) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    createRollup: async (payload: any) => {
      try {
        const response = await apiInstances.data
          .post(`questions/rollups/save`, {
            json: payload,
          })
          .json();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    updateQuestionOptionColor: async (
      questionGroup: string,
      optionId: number,
      color: string,
      entityType: "QUESTION_OPTION" | "ROLLUP" = "QUESTION_OPTION",
    ) => {
      try {
        const response = await apiInstances.data
          .put(`questions/customize/${questionGroup}`, {
            json: {
              color,
              entityId: optionId,
              entityType,
              sequence: 0,
            },
          })
          .text();
        return response === "Customization updated successfully."
          ? true
          : false;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
  },
  getIngestionTime: async (markets: number[]) => {
    try {
      const response = await apiInstances.data
        .post("updated-time", {
          json: { geoIds: markets },
        })
        .json();
      return response;
    } catch (error: unknown) {}
  },
  export: {
    exclusiveQuestionSegmentPptx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.data
          .post("segment/export?fileType=pptx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    exclusiveQuestionSegmentxlsx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.export
          .post("segment/export?fileType=xlsx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    exclusiveQuestionBrandPptx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.data
          .post("brand/export?fileType=pptx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    exclusiveQuestionBrandxlsx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.export
          .post("brand/export?fileType=xlsx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getxlsxExport: async (requestPayload: any, path: string) => {
      try {
        const response = await apiInstances.export
          .post(`${path}/export?fileType=xlsx`, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getpptExport: async (requestPayload: any, path: string) => {
      try {
        const response = await apiInstances.export
          .post(`${path}/export?fileType=xlsx`, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
  },
  butterfly: async (requestData: EquityDataRequestForAPIClient) => {
    const url = "butterfly";
    try {
      const requestPayload = getRequestObjectForAPI(requestData);
      const response = await apiInstances.data
        .post(url, {
          json: requestPayload,
          signal: getAbortSignalForKey(url),
        })
        .json();
      return { response, metaData: requestPayload };
    } catch (err: unknown) {
      if ((err as any).name === "AbortError") {
        return "";
      }
      return { error: "Something went wrong", errorCode: 500 };
    } finally {
      clearAbortController(url);
    }
  },
  differentiation: async (requestData: EquityDataRequestForAPIClient) => {
    const url = "da";
    try {
      const requestPayload = getRequestObjectForAPI(requestData);
      const response: any = await apiInstances.data
        .post(url, {
          json: requestPayload,
          signal: getAbortSignalForKey(url),
        })
        .json();
      if (response?.length === 0) {
        return { error: "No data found", errorCode: 500 };
      }
      return { response, metaData: requestPayload };
    } catch (err: unknown) {
      if ((err as any).name === "AbortError") {
        return "";
      }
      return { error: "Something went wrong", errorCode: 500 };
    } finally {
      clearAbortController(url);
    }
  },
  perceptual: async (requestData: EquityDataRequestForAPIClient) => {
    const url = "ca";
    try {
      const requestPayload = getRequestObjectForAPI(requestData);
      const response: any = await apiInstances.data
        .post(url, {
          json: requestPayload,
          signal: getAbortSignalForKey(url),
        })
        .json();
      if (response?.length === 0) {
        return { error: "No data found", errorCode: 500 };
      }
      return {
        response: response,
        metaData: requestPayload,
      };
    } catch (err: unknown) {
      if ((err as any).name === "AbortError") {
        return "";
      }
      return { error: "Something went wrong", errorCode: 500 };
    } finally {
      clearAbortController(url);
    }
  },
  profile: async (requestData: EquityDataRequestForAPIClient) => {
    const url = "profile";
    try {
      const requestPayload = getRequestObjectForAPI(requestData);
      const response = await apiInstances.data
        .post(url, {
          json: requestPayload,
          signal: getAbortSignalForKey(url),
        })
        .json();
      return {
        response: response,
        metaData: requestPayload,
      };
    } catch (err: unknown) {
      if ((err as any).name === "AbortError") {
        return "";
      }
      return { error: "Something went wrong", errorCode: 500 };
    } finally {
      clearAbortController(url);
    }
  },
};

export default dataService;
